import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Typography } from '@mui/material';
import theme from '../../../theme/theme';
import { formatDate, DATE_FORMAT } from '../../../lib/date/formatDate';
import { formatBoolean } from '../../../lib/formatBoolean';
import { TruncatedCell } from './TruncatedCell';

// ── Context ────────────────────────────────────────────────────────────────────

export type RenderContext = { t: TFunction };

// ── Shared cell style ─────────────────────────────────────────────────────────

export const cellSx = { fontSize: '0.85rem', color: theme.palette.text.body } as const;

/** Reusable bold override for any table cell — pass as `renderCell`'s `sx` arg. */
export const boldCellSx = { fontWeight: 'bold' } as const;

// ── Registry ──────────────────────────────────────────────────────────────────
// Add a new entry here to support a new cell type — no other file needs to change.

export const CELL_RENDERERS = {
  text: (value: unknown, _ctx: RenderContext, sx?: object) => (
    <TruncatedCell text={String(value ?? '')} sx={{ ...cellSx, ...sx }} />
  ),
  date: (value: unknown, _ctx: RenderContext, sx?: object) => (
    <Typography sx={{ ...cellSx, whiteSpace: 'nowrap', ...sx }}>
      {formatDate(String(value ?? ''), DATE_FORMAT)}
    </Typography>
  ),
  boolean: (value: unknown, ctx: RenderContext, sx?: object) => (
    <Typography sx={{ ...cellSx, ...sx }}>
      {typeof value === 'boolean' ? formatBoolean(value, ctx.t) : String(value ?? '')}
    </Typography>
  ),
} satisfies Record<string, (value: unknown, ctx: RenderContext, sx?: object) => ReactNode>;

export type CellRendererType = keyof typeof CELL_RENDERERS;

// ── Single dispatcher ─────────────────────────────────────────────────────────

/** `sx` is merged over the shared `cellSx` (e.g. `{ fontWeight: 'bold' }`). */
export function renderCell(
  type: CellRendererType,
  value: unknown,
  ctx: RenderContext,
  sx?: object,
): ReactNode {
  return CELL_RENDERERS[type](value, ctx, sx);
}
